// Code-block with a small Copy button in the top-right corner. In Guide
// mode the model often hands the user a multi-line snippet to paste
// somewhere — reading and retyping is impractical, so we make copy one
// click. Used by MarkdownText's `pre` element override.
//
// Lives in its own module rather than inside MarkdownText.jsx so Jest can
// import + render it without dragging in @assistant-ui/react-markdown
// (ESM-only, CRA-5 + Jest can't transform it).

import React, { useRef, useState, useCallback } from "react";
import { Copy, Check } from "lucide-react";
import { useCopilotController } from "../CopilotControllerContext";
import { pushTextToVmClipboard } from "../guacamoleHook";

export function CodeBlock({ children, ...rest }) {
  // Controller carries the active VM iframe — needed to also push the
  // copied text into Guacamole's VM clipboard (host clipboard alone isn't
  // enough for right-click → paste inside the guest; see guacamoleHook
  // pushTextToVmClipboard for the why).
  const controller = useCopilotController();
  const preRef = useRef(null);
  const [copied, setCopied] = useState(false);

  const onCopy = useCallback(async () => {
    // jsdom's innerText support is partial; textContent is the universal
    // fallback (browsers return effectively the same string for a <pre>
    // whose only content is plain text or <code>).
    const text =
      preRef.current?.innerText ||
      preRef.current?.textContent ||
      "";
    if (!text) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for non-secure contexts (older browsers, http://).
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      // Also mirror into the VM clipboard via Guacamole. Silent no-op if
      // the iframe / Guacamole client isn't reachable — Ctrl+Shift+V
      // (paste from host clipboard) still works in that case.
      pushTextToVmClipboard(controller?.iframe, text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      // Clipboard API can fail under iframe sandboxing — surface nothing
      // rather than throwing; the user can still select+copy manually.
    }
  }, [controller]);

  return (
    <div className="tw-relative tw-my-2 tw-group">
      <pre
        ref={preRef}
        className="tw-overflow-x-auto tw-rounded-md tw-bg-muted tw-p-2 tw-pr-9 tw-text-xs tw-text-foreground"
        {...rest}
      >
        {children}
      </pre>
      <button
        type="button"
        onClick={onCopy}
        aria-label={copied ? "Copied" : "Copy code"}
        title={copied ? "Copied" : "Copy code"}
        className="copilot-codeblock-copy"
      >
        {copied
          ? <Check className="tw-h-3.5 tw-w-3.5 tw-text-emerald-400" />
          : <Copy className="tw-h-3.5 tw-w-3.5" />}
      </button>
    </div>
  );
}
